import React from 'react';
import { Plus, ShoppingCart, ExternalLink, Eye } from 'lucide-react';
import { Product } from '../types';

interface ProductCardProps {
  product: Product;
  onAddToCart?: (productId: string) => void;
  onProductClick?: (productId: string) => void;
  showCartButton?: boolean;
  showAnalytics?: boolean;
  isLoading?: boolean;
}

export function ProductCard({ 
  product, 
  onAddToCart, 
  onProductClick,
  showCartButton = true,
  showAnalytics = false,
  isLoading = false 
}: ProductCardProps) {
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(price);
  };

  const handleProductClick = () => {
    if (onProductClick) {
      onProductClick(product.id);
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (product.isAffiliate && product.affiliateUrl) {
      // Track click and redirect to affiliate URL
      if (onProductClick) {
        onProductClick(product.id);
      }
      window.open(product.affiliateUrl, '_blank');
    } else if (onAddToCart) {
      // Add to cart for regular products
      onAddToCart(product.id);
    }
  };

  return (
    <div 
      className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
      onClick={handleProductClick}
    >
      <div className="aspect-square overflow-hidden relative">
        <img
          src={product.image}
          alt={product.name}
          className="w-full h-full object-cover"
        />
        {product.isAffiliate && (
          <div className="absolute top-2 right-2 bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full font-medium">
            {product.affiliateProvider ? 
              `${product.affiliateProvider.charAt(0).toUpperCase()}${product.affiliateProvider.slice(1)}` : 
              'Affiliate'}
          </div>
        )}
        {/* Always show product number */}
        <div className="absolute top-2 left-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded-full font-medium">
          #{product.productNumber}
        </div>
      </div>
      
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
          {product.name}
        </h3>
        <p className="text-sm text-gray-600 mb-3 line-clamp-2">
          {product.description}
        </p>
        
        {showAnalytics && (
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
            <div className="flex items-center gap-1">
              <Eye className="w-4 h-4" />
              <span>{product.analytics.views} views</span>
            </div>
            <div className="flex items-center gap-1">
              <ExternalLink className="w-4 h-4" />
              <span>{product.analytics.clicks} clicks</span>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <div className="text-lg font-bold text-blue-600">
            {formatPrice(product.price)}
          </div>
          
          {showCartButton && (
            <button
              onClick={handleActionClick}
              disabled={isLoading || !product.inStock}
              className={`p-2 rounded-full shadow-lg transform transition-all duration-200 hover:scale-105 active:scale-95 ${
                product.isAffiliate
                  ? 'bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white'
              }`}
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : product.isAffiliate ? (
                <ExternalLink className="w-5 h-5" />
              ) : (
                <Plus className="w-5 h-5" />
              )}
            </button>
          )}
        </div>
        
        {!product.inStock && (
          <div className="mt-2 text-xs text-red-500 font-medium">
            {product.isAffiliate ? 'Tidak tersedia' : 'Stok habis'}
          </div>
        )}
      </div>
    </div>
  );
}